import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '../../../context/useAuth'
import { useNls } from '../../../nls/nlsProvider'
import { Button, AppDialog, HiddenFileInput, Select, TextInput, type SelectOption } from '../../Common'
import { StatusPill } from '../../Common/StatusPill'
import { BarcodeScannerShell } from '../BarcodeScannerShell'
import { OpsRecordModalForm } from '../../Ops/registers/OpsRecordModalForm'
import {
  subscribeDues,
  recordCheckpointScan,
  skipDue,
} from '../../../services/opsSchedulesService'
import {
  submitRecord,
  subscribeToTemplate,
  subscribeToTemplateVersions,
} from '../../../services/opsRegistersService'
import {
  listRequestKinds,
  subscribeOpsRequests,
  raiseRequest,
  startRequest,
  markRequestDone,
} from '../../../services/opsRequestsService'
import {
  subscribeAttendance,
  markAttendanceRoll,
} from '../../../services/opsRostersService'
import {
  subscribeToWorkspacePlaces,
  resolvePlaceToken,
} from '../../../services/opsPlacesService'
import { subscribeToBedStatesForBeds } from '../../../services/opsBedStatesService'
import { uploadRequestPhoto } from '../../../services/opsRequestPhotosService'
import {
  queueOpsDueRecord,
  queueOpsCheckpointScan,
  queueOpsSkipDue,
  queueOpsRaiseRequest,
  queueOpsAttendanceRoll,
  queueOpsStartRequest,
  queueOpsMarkRequestDone,
  queueOpsConIssueCreate,
  removeMutation,
} from '../../../services/helper/queueService'
import { createOpId } from '../../../services/helper/opId'
import { listConProjects } from '../../../services/conProjectsService'
import { createConIssue, attachConIssuePhotos } from '../../../services/conIssuesService'
import { uploadConIssuePhoto } from '../../../services/conIssuePhotosService'
import { unitSubtreeIds } from '../../Ops/hospital/hospitalTodayHelpers'
import { BmwPhoneSection } from '../../Ops/hospital/BmwPhoneSection'
import { OnCallPhoneSection } from '../../Ops/hospital/OnCallPhoneSection'
import { DprPhoneSection } from '../../Ops/construction/DprPhoneSection'
import { PlantHelperPhoneSection } from '../../Ops/plant/PlantHelperPhoneSection'
import { subscribeOpsAssets } from '../../../services/opsAssetsService'
import type { OpsAsset, OpsBedState } from '../../../types/ops'
import {
  getHelperSnapshot,
  getHelperOpsPhoneItems,
  subscribeHelperStore,
  type HelperStoreSnapshot,
} from '../../../services/helper/helperStore'
import { getApiErrorMessage } from '../../../services/apiConnectivity'
import {
  getSharedDeviceWorkRouterContext,
  routeRegisterRecord,
  routeCheckpointScan,
  routeSkipDue,
  routeRaiseRequest,
  routeAttendanceRoll,
  routeStartRequest,
  routeMarkRequestDone,
} from '../../../services/sharedDeviceWorkRouter'
import { filterDueNow } from '../../../utils/opsSchedules'
import type {
  OpsDue,
  OpsRequest,
  OpsRequestKind,
  OpsPlace,
  OpsAttendance,
  RaiseRequestInput,
  MarkAttendanceRollInput,
  OpsRequestSector,
  OpsTemplate,
  OpsTemplateVersion,
  ConResponsiblePartyType,
  ConImpactLevel,
  ConProject,
} from '../../../types/ops'
import {
  validateConIssueTitle,
  validateConIssueDescription,
  validateConResponsibleParty,
  validateConImpact,
  CON_RESPONSIBLE_PARTY_TYPES,
  CON_IMPACT_LEVELS,
  DUE_CHECK_UNIT_FILTER_LEVELS,
  MAX_CON_ISSUE_PHOTOS,
} from '../../../types/ops'
import type { OpsPhoneItemKey } from '../../../../functions-common/src/ops/phoneItems'
import type { HelperMutationRecord, OpsConIssueCreatePayload } from '../../../services/helper/helperOutboxDb'
import {
  getActivePersonCache,
  subscribeActivePersonCache,
  updateActivePersonCache,
  type SharedDevicePersonSnapshot,
  type OpsCachedTemplate,
} from '../../../services/sharedDevicePersonCache'
import {
  getStoredSharedDeviceCredentials,
  useSharedDeviceClaim,
} from '../../../services/opsSharedDevicesService'
import styles from './HelperOpsTab.module.css'

export interface HelperOpsTabProps {
  workspaceId: string
  initialRequestId?: string
  isSharedDevice?: boolean
  devicePlaceId?: string
}

function getTodayDayKey(): string {
  const d = new Date()
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function HelperOpsTab({
  workspaceId,
  initialRequestId: propRequestId,
  isSharedDevice: propIsSharedDevice,
  devicePlaceId: propDevicePlaceId,
}: HelperOpsTabProps): React.ReactElement {
  const { t } = useNls()
  const { currentUser } = useAuth()
  const sharedCtx = getSharedDeviceWorkRouterContext()
  const sharedClaim = useSharedDeviceClaim()
  const storedCreds = getStoredSharedDeviceCredentials()
  const isSharedDevice = Boolean(
    propIsSharedDevice ?? (sharedCtx !== null || sharedClaim || storedCreds !== null),
  )
  const effectiveDevicePlaceId = propDevicePlaceId || storedCreds?.placeId || ''
  const currentUid = sharedCtx?.personUid || currentUser?.uid || ''

  // Deep-link targeted request
  const effectiveRequestId = useMemo(() => {
    if (propRequestId) return propRequestId
    if (typeof window !== 'undefined' && window.location) {
      const sp = new URLSearchParams(window.location.search)
      return sp.get('request') || undefined
    }
    return undefined
  }, [propRequestId])

  const isOffline = typeof navigator !== 'undefined' && !navigator.onLine

  const [offlineCacheSnapshot, setOfflineCacheSnapshot] = useState<SharedDevicePersonSnapshot | null>(
    () => (isSharedDevice && (isOffline || !currentUser) ? getActivePersonCache()?.snapshot ?? null : null),
  )

  // Allowed phone items for this helper (read from helperStore, populated by layout - F3)
  const [allowedItems, setAllowedItems] = useState<OpsPhoneItemKey[]>(() =>
    getHelperOpsPhoneItems(workspaceId),
  )
  const [itemsLoaded, setItemsLoaded] = useState(true)

  // Check-in scanner
  const [scannerOpen, setScannerOpen] = useState(false)

  // Dues
  const [dues, setDues] = useState<OpsDue[]>([])
  const [dueActionBusy, setDueActionBusy] = useState<string | null>(null)
  const [dueError, setDueError] = useState<string | null>(null)
  const [skippingDue, setSkippingDue] = useState<OpsDue | null>(null)
  const [skipReason, setSkipReason] = useState('')
  const [scanningCheckpointDue, setScanningCheckpointDue] = useState<OpsDue | null>(null)
  const [recordingDue, setRecordingDue] = useState<OpsDue | null>(null)

  // Requests
  const [requestKinds, setRequestKinds] = useState<OpsRequestKind[]>([])
  const [places, setPlaces] = useState<OpsPlace[]>([])
  const [myRequests, setMyRequests] = useState<OpsRequest[]>([])
  const [selectedKindId, setSelectedKindId] = useState<string>('')
  const [selectedPlaceId, setSelectedPlaceId] = useState<string>('')
  const [requestTitle, setRequestTitle] = useState('')
  const [requestBusy, setRequestBusy] = useState(false)
  const [requestError, setRequestError] = useState<string | null>(null)
  const [requestSuccess, setRequestSuccess] = useState<string | null>(null)

  // Attendance roll
  const [attendanceRecords, setAttendanceRecords] = useState<OpsAttendance[]>([])
  const [rollBusyId, setRollBusyId] = useState<string | null>(null)
  const [rollError, setRollError] = useState<string | null>(null)

  // Cache templates and latest approved versions for floor dues
  const [cachedTemplates, setCachedTemplates] = useState<OpsCachedTemplate[]>([])

  // Reset form and close dialogs on switch-out or person switch
  const prevUidRef = React.useRef(currentUid)
  useEffect(() => {
    const handleSwitchOut = () => {
      setRecordingDue(null)
      setScanningCheckpointDue(null)
      setSkippingDue(null)
      setScannerOpen(false)
      setRequestTitle('')
      setSkipReason('')
      setRequestError(null)
      setRequestSuccess(null)
      setDueError(null)
      setRollError(null)
      setSelectedKindId('')
      setSelectedPlaceId('')
      setDues([])
      setCachedTemplates([])
      setMyRequests([])
      setAttendanceRecords([])
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('shared-device:switch-out', handleSwitchOut)
      return () => window.removeEventListener('shared-device:switch-out', handleSwitchOut)
    }
  }, [])

  useEffect(() => {
    if (prevUidRef.current !== currentUid) {
      prevUidRef.current = currentUid
      setRecordingDue(null)
      setScanningCheckpointDue(null)
      setSkippingDue(null)
      setScannerOpen(false)
      setRequestTitle('')
      setSkipReason('')
      setRequestError(null)
      setRequestSuccess(null)
      setDueError(null)
      setRollError(null)
      setSelectedKindId('')
      setSelectedPlaceId('')
      setDues([])
      setCachedTemplates([])
      setMyRequests([])
      setAttendanceRecords([])
    }
  }, [currentUid])

  useEffect(() => {
    if (!isSharedDevice || (!isOffline && currentUser)) return undefined
    return subscribeActivePersonCache((snap) => {
      setOfflineCacheSnapshot(snap)
    })
  }, [isSharedDevice, isOffline, currentUser])

  const shouldUseOfflineCache =
    isSharedDevice && (isOffline || !currentUser) && Boolean(offlineCacheSnapshot)

  useEffect(() => {
    const unsub = subscribeHelperStore((snap) => {
      const items = workspaceId
        ? (snap.opsPhoneItemsByWorkspace?.[workspaceId] ?? getHelperOpsPhoneItems(workspaceId))
        : snap.opsPhoneItems
      setAllowedItems(items ?? [])
      setItemsLoaded(true)
    })
    return () => unsub()
  }, [workspaceId])

  const effectiveAllowedItems = useMemo(() => {
    if (shouldUseOfflineCache && offlineCacheSnapshot?.opsPhoneItems) {
      return offlineCacheSnapshot.opsPhoneItems
    }
    return allowedItems
  }, [shouldUseOfflineCache, offlineCacheSnapshot, allowedItems])

  const canCheckIn = effectiveAllowedItems.includes('h.checkIn')
  const canDueChecks = effectiveAllowedItems.includes('h.dueChecks')
  const canRequest = effectiveAllowedItems.includes('h.request')
  const canRoll = effectiveAllowedItems.includes('h.roll')
  const canBmw = effectiveAllowedItems.includes('h.bmw')
  const canOnCall = effectiveAllowedItems.includes('h.oncall')
  const canDpr = effectiveAllowedItems.includes('h.dpr')
  const canHindrance = effectiveAllowedItems.includes('h.hindrance')
  const canPlant = effectiveAllowedItems.includes('h.plant')

  const handleScanPlace = useCallback((scannedText: string) => {
    let token = scannedText.trim()
    if (token.includes('/pq/')) {
      token = token.split('/pq/')[1]?.split('?')[0] || token
    } else if (token.includes('/q/')) {
      token = token.split('/q/')[1]?.split('?')[0] || token
    }
    setScannerOpen(false)
    if (typeof window !== 'undefined' && window.location) {
      window.location.assign(`/pq/${encodeURIComponent(token)}`)
    }
  }, [])

  useEffect(() => {
    if (!workspaceId || !canDueChecks || !currentUid) return
    const now = Date.now()
    const from = now - 24 * 3600_000
    const to = now + 24 * 3600_000

    const unsub = subscribeDues(
      workspaceId,
      {
        workspaceId,
        from,
        to,
        callerUid: currentUid,
        isRestricted: false,
      },
      (fetched: OpsDue[]) => {
        setDues(fetched)
      },
      (err) => {
        setDueError(getApiErrorMessage(err))
      },
    )
    return () => unsub()
  }, [workspaceId, canDueChecks, currentUid])

  const [plantAssets, setPlantAssets] = useState<OpsAsset[]>([])
  useEffect(() => {
    if (!workspaceId || !canPlant) return
    const unsub = subscribeOpsAssets(
      workspaceId,
      { callerUid: currentUid },
      (loaded: OpsAsset[]) => setPlantAssets(loaded),
      (err: Error) => console.warn('[HelperOpsTab] subscribeOpsAssets failed:', err),
    )
    return () => unsub()
  }, [workspaceId, canPlant])

  const effectiveDues = useMemo(() => {
    if (shouldUseOfflineCache && offlineCacheSnapshot?.opsDues) {
      return offlineCacheSnapshot.opsDues
    }
    return dues
  }, [shouldUseOfflineCache, offlineCacheSnapshot, dues])

  const floorDues = useMemo(() => {
    if (isSharedDevice && effectiveDevicePlaceId) {
      return effectiveDues.filter((d) => !d.placeId || d.placeId === effectiveDevicePlaceId)
    }
    return effectiveDues
  }, [effectiveDues, isSharedDevice, effectiveDevicePlaceId])

  const unitFilterPlaces = useMemo(() => {
    return places.filter((p) =>
      (DUE_CHECK_UNIT_FILTER_LEVELS as readonly string[]).includes(p.level),
    )
  }, [places])

  const [selectedDueUnitId, setSelectedDueUnitId] = useState<string>('')

  const dueSubtree = useMemo(() => {
    if (!selectedDueUnitId) return null
    return unitSubtreeIds(places, selectedDueUnitId)
  }, [places, selectedDueUnitId])

  const filteredDuesByUnit = useMemo(() => {
    if (!dueSubtree) return floorDues
    return floorDues.filter((d) => d.placeId && dueSubtree.has(d.placeId))
  }, [floorDues, dueSubtree])

  const displayedDues = useMemo(() => {
    try {
      const filtered = filterDueNow(filteredDuesByUnit)
      return filtered.length > 0
        ? filtered
        : filteredDuesByUnit.filter((d) => d.status === 'OPEN')
    } catch {
      return filteredDuesByUnit.filter((d) => d.status === 'OPEN')
    }
  }, [filteredDuesByUnit])

  const handleRecordDue = (due: OpsDue) => {
    setDueError(null)
    setRecordingDue(due)
  }

  const handleScanCheckpoint = (due: OpsDue) => {
    setDueError(null)
    setScanningCheckpointDue(due)
  }

  const handleCheckpointScanned = async (token: string) => {
    if (!scanningCheckpointDue) return
    const due = scanningCheckpointDue
    setScanningCheckpointDue(null)
    setDueActionBusy(due.id)
    setDueError(null)
    const opId = createOpId()
    const routerCtx = getSharedDeviceWorkRouterContext()

    const payload = {
      workspaceId,
      token,
      opId,
      clientMutationId: opId,
    }

    try {
      if (routerCtx) {
        await routeCheckpointScan(payload, routerCtx)
      } else if (typeof navigator !== 'undefined' && !navigator.onLine) {
        await queueOpsCheckpointScan(payload)
      } else {
        await recordCheckpointScan({
          workspaceId,
          token,
          opId,
        })
      }
    } catch (err) {
      setDueError(getApiErrorMessage(err))
    } finally {
      setDueActionBusy(null)
    }
  }

  const handleConfirmSkipDue = async () => {
    if (!skippingDue) return
    const opId = createOpId()
    setDueActionBusy(skippingDue.id)
    const routerCtx = getSharedDeviceWorkRouterContext()
    try {
      if (routerCtx) {
        await routeSkipDue(
          {
            workspaceId,
            dueId: skippingDue.id,
            reason: skipReason.trim(),
            opId,
            clientMutationId: opId,
          },
          routerCtx,
        )
      } else if (typeof navigator !== 'undefined' && !navigator.onLine) {
        await queueOpsSkipDue({
          workspaceId,
          dueId: skippingDue.id,
          reason: skipReason.trim(),
          clientMutationId: opId,
        })
      } else {
        await skipDue({
          workspaceId,
          dueId: skippingDue.id,
          reason: skipReason.trim(),
          opId,
        })
      }
      setSkippingDue(null)
      setSkipReason('')
    } catch (err) {
      setDueError(getApiErrorMessage(err))
    } finally {
      setDueActionBusy(null)
    }
  }

  useEffect(() => {
    if (!workspaceId || (!canDueChecks && !canRequest && !canBmw)) return
    const unsub = subscribeToWorkspacePlaces(
      workspaceId,
      (plist: OpsPlace[]) => setPlaces(plist || []),
    )
    return () => unsub()
  }, [workspaceId, canDueChecks, canRequest, canBmw])

  // Requests
  useEffect(() => {
    if (!workspaceId || !canRequest) return
    let active = true

    listRequestKinds({ workspaceId })
      .then((res) => {
        if (!active) return
        setRequestKinds(res.kinds || [])
      })
      .catch((err) => {
        if (active) setRequestError(getApiErrorMessage(err))
      })

    const unsubReqs = subscribeOpsRequests(
      workspaceId,
      { callerUid: currentUid },
      (rlist: OpsRequest[]) => {
        if (!active) return
        setMyRequests(rlist || [])
      },
      (err) => {
        if (!active) return
        setRequestError(getApiErrorMessage(err))
      },
    )

    return () => {
      active = false
      unsubReqs()
    }
  }, [workspaceId, canRequest, currentUid])

  const selectedPlace = useMemo(() => {
    if (isSharedDevice && effectiveDevicePlaceId) {
      const match = places.find((p) => p.id === effectiveDevicePlaceId)
      if (match) return match
      return {
        id: effectiveDevicePlaceId,
        name: effectiveDevicePlaceId,
        sector: 'construction',
        level: 'site',
      } as unknown as OpsPlace
    }
    return places.find((p) => p.id === selectedPlaceId) || places[0] || null
  }, [isSharedDevice, effectiveDevicePlaceId, places, selectedPlaceId])
  const placeSector = (selectedPlace?.sector as OpsRequestSector) || 'construction'

  const effectiveMyRequests = useMemo(() => {
    if (shouldUseOfflineCache && offlineCacheSnapshot?.opsRequests) {
      return offlineCacheSnapshot.opsRequests
    }
    return myRequests
  }, [shouldUseOfflineCache, offlineCacheSnapshot, myRequests])

  const effectiveRequestKinds = useMemo(() => {
    if (shouldUseOfflineCache && offlineCacheSnapshot?.opsRequestKinds) {
      return offlineCacheSnapshot.opsRequestKinds
    }
    return requestKinds
  }, [shouldUseOfflineCache, offlineCacheSnapshot, requestKinds])

  const filteredKinds = useMemo(
    () => effectiveRequestKinds.filter((k) => !k.sector || k.sector === placeSector),
    [effectiveRequestKinds, placeSector],
  )

  const effectiveKindId =
    selectedKindId && filteredKinds.some((k) => k.key === selectedKindId)
      ? selectedKindId
      : (filteredKinds[0]?.key || '')

  const effectivePlaceId =
    isSharedDevice && effectiveDevicePlaceId
      ? effectiveDevicePlaceId
      : selectedPlace?.id || selectedPlaceId || ''

  const displayedRequests = useMemo(() => {
    if (isSharedDevice && effectiveDevicePlaceId) {
      return effectiveMyRequests.filter(
        (r) =>
          r.placeId === effectiveDevicePlaceId &&
          r.state !== 'DONE' &&
          r.state !== 'VERIFIED' &&
          r.state !== 'CANCELLED',
      )
    }
    return effectiveMyRequests
  }, [effectiveMyRequests, isSharedDevice, effectiveDevicePlaceId])

  // Bed states for bed requests in displayedRequests
  const bedPlaceIds = useMemo(() => {
    const ids = new Set<string>()
    for (const r of displayedRequests) {
      if (!r.placeId) continue
      const targetPlaceId = r.placeId
      const p = places.find((pl) => pl.id === targetPlaceId)
      if (p && p.level === 'BED' && p.id) {
        ids.add(p.id)
      }
    }
    return Array.from(ids)
  }, [displayedRequests, places])

  const [requestBedStates, setRequestBedStates] = useState<OpsBedState[]>([])
  useEffect(() => {
    if (!workspaceId || bedPlaceIds.length === 0) {
      return
    }
    const unsub = subscribeToBedStatesForBeds(
      workspaceId,
      bedPlaceIds,
      (list) => setRequestBedStates(list),
      (err) => console.error('[HelperOpsTab] subscribeToBedStatesForBeds failed', err),
      { callerUid: currentUid, isRestricted: false },
    )
    return () => unsub()
  }, [workspaceId, bedPlaceIds, currentUid])

  const bedStatesMap = useMemo(() => {
    const map = new Map<string, OpsBedState>()
    for (const s of requestBedStates) {
      map.set(s.placeId, s)
    }
    return map
  }, [requestBedStates])

  // Assigned Work: Start and Done
  const [startBusyId, setStartBusyId] = useState<string | null>(null)
  const [doneTargetRequest, setDoneTargetRequest] = useState<OpsRequest | null>(null)
  const [donePhotoFiles, setDonePhotoFiles] = useState<File[]>([])
  const [isDonePhotoRequired, setIsDonePhotoRequired] = useState(false)
  const [doneBusy, setDoneBusy] = useState(false)

  const handleStartRequest = async (req: OpsRequest) => {
    setStartBusyId(req.id)
    setRequestError(null)
    const opId = createOpId()
    const routerCtx = getSharedDeviceWorkRouterContext()
    try {
      if (routerCtx) {
        await routeStartRequest(
          {
            workspaceId,
            requestId: req.id,
            opId,
            clientMutationId: opId,
          },
          routerCtx,
        )
      } else if (typeof navigator !== 'undefined' && !navigator.onLine) {
        await queueOpsStartRequest({
          workspaceId,
          requestId: req.id,
          opId,
          clientMutationId: opId,
        })
      } else {
        await startRequest({
          workspaceId,
          requestId: req.id,
          opId,
        })
      }
    } catch (err) {
      setRequestError(getApiErrorMessage(err))
    } finally {
      setStartBusyId(null)
    }
  }

  const handleOpenDone = (req: OpsRequest) => {
    const kind = effectiveRequestKinds.find(
      (k) => k.key === req.kind || k.key === req.kindSnapshot?.key,
    )
    const isPhotoRequired = Boolean(kind?.photoEnabled ?? req.kindSnapshot?.photoEnabled)
    setDoneTargetRequest(req)
    setDonePhotoFiles([])
    setIsDonePhotoRequired(isPhotoRequired)
  }

  const handleConfirmDone = async () => {
    if (!doneTargetRequest) return
    if (isDonePhotoRequired && donePhotoFiles.length === 0) {
      setRequestError(t('NLSHelperOpsDonePhotoRequired') || 'Photo required')
      return
    }
    setDoneBusy(true)
    setRequestError(null)
    const opId = createOpId()
    const routerCtx = getSharedDeviceWorkRouterContext()
    const isOffline = typeof navigator !== 'undefined' && !navigator.onLine

    try {
      if (routerCtx) {
        await routeMarkRequestDone(
          {
            workspaceId,
            requestId: doneTargetRequest.id,
            opId,
            clientMutationId: opId,
          },
          routerCtx,
          donePhotoFiles.length > 0 ? donePhotoFiles : undefined,
        )
      } else if (isOffline) {
        await queueOpsMarkRequestDone(
          {
            workspaceId,
            requestId: doneTargetRequest.id,
            opId,
            clientMutationId: opId,
          },
          donePhotoFiles.length > 0 ? donePhotoFiles : undefined,
        )
      } else {
        const uploadedPaths: string[] = []
        for (const file of donePhotoFiles) {
          const up = await uploadRequestPhoto(
            workspaceId,
            doneTargetRequest.id,
            file,
          )
          uploadedPaths.push(up.path)
        }
        const photos = uploadedPaths.length > 0 ? uploadedPaths : undefined
        await markRequestDone({
          workspaceId,
          requestId: doneTargetRequest.id,
          photos,
          opId,
        })
      }
      setDoneTargetRequest(null)
      setDonePhotoFiles([])
    } catch (err) {
      setRequestError(getApiErrorMessage(err))
    } finally {
      setDoneBusy(false)
    }
  }

  // Raise form place scanner
  const [raiseScannerOpen, setRaiseScannerOpen] = useState(false)

  const handleScanPlaceForRaise = async (scannedText: string) => {
    let token = scannedText.trim()
    if (token.includes('/pq/')) {
      token = token.split('/pq/')[1]?.split('?')[0] || token
    } else if (token.includes('/q/')) {
      token = token.split('/q/')[1]?.split('?')[0] || token
    }
    setRaiseScannerOpen(false)
    const directMatch = places.find((p) => p.id === token)
    if (directMatch && directMatch.id) {
      setSelectedPlaceId(directMatch.id)
      return
    }
    try {
      const res = await resolvePlaceToken({ token })
      if (res?.placeId) {
        setSelectedPlaceId(res.placeId)
      }
    } catch {
      setSelectedPlaceId(token)
    }
  }

  const handleRaiseRequest = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!requestTitle.trim()) return
    setRequestBusy(true)
    setRequestError(null)
    setRequestSuccess(null)
    const opId = createOpId()
    const routerCtx = getSharedDeviceWorkRouterContext()

    const kindKey = effectiveKindId || 'general'
    const requestPayload: RaiseRequestInput = {
      workspaceId,
      sector: placeSector,
      kind: kindKey,
      placeId: effectivePlaceId,
      title: requestTitle.trim(),
      opId,
    }

    try {
      if (routerCtx) {
        await routeRaiseRequest(
          {
            ...requestPayload,
            clientMutationId: opId,
          },
          routerCtx,
        )
      } else if (typeof navigator !== 'undefined' && !navigator.onLine) {
        await queueOpsRaiseRequest({
          ...requestPayload,
          clientMutationId: opId,
        })
      } else {
        await raiseRequest(requestPayload)
      }
      setRequestTitle('')
      setRequestSuccess(t('NLSHelperOpsRequestSubmitted'))
    } catch (err) {
      setRequestError(getApiErrorMessage(err))
    } finally {
      setRequestBusy(false)
    }
  }

  // h.hindrance state
  const [hindranceProjects, setHindranceProjects] = useState<ConProject[]>([])
  const [hindranceProjectId, setHindranceProjectId] = useState<string>('')
  const [hindrancePlaceId, setHindrancePlaceId] = useState<string>('')
  const [hindranceDescription, setHindranceDescription] = useState<string>('')
  const [hindrancePartyType, setHindrancePartyType] = useState<ConResponsiblePartyType>('INTERNAL')
  const [hindranceImpactTime, setHindranceImpactTime] = useState<ConImpactLevel>('POSSIBLE')
  const [hindranceImpactCost, setHindranceImpactCost] = useState<ConImpactLevel>('NONE')
  const [hindranceScannerOpen, setHindranceScannerOpen] = useState(false)
  const [hindranceBusy, setHindranceBusy] = useState(false)
  const [hindranceError, setHindranceError] = useState<string | null>(null)
  const [hindranceSuccess, setHindranceSuccess] = useState<string | null>(null)
  const [hindrancePhotos, setHindrancePhotos] = useState<File[]>([])
  const [hindrancePhotoFailure, setHindrancePhotoFailure] = useState<{
    count: number
    failedFiles: File[]
    issueId: string
  } | null>(null)
  const hindranceFileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    let ignore = false
    if (!workspaceId || !canHindrance) return
    void listConProjects({ workspaceId, status: 'ACTIVE' })
      .then((res) => {
        if (ignore) return
        const activeProjects = res.projects || []
        setHindranceProjects(activeProjects)
        if (activeProjects.length > 0 && !hindranceProjectId) {
          const firstProj = activeProjects[0]
          if (firstProj?.id) {
            setHindranceProjectId(firstProj.id)
            if (firstProj.placeId && !hindrancePlaceId) {
              setHindrancePlaceId(firstProj.placeId)
            }
          }
        }
      })
      .catch((err: unknown) => {
        if (ignore) return
        console.warn('[HelperOpsTab] Failed to list construction projects for hindrance:', err)
      })
    return () => {
      ignore = true
    }
  }, [workspaceId, canHindrance, hindranceProjectId, hindrancePlaceId])

  const selectedHindranceProject = useMemo(() => {
    return hindranceProjects.find((p) => p.id === hindranceProjectId) || hindranceProjects[0] || null
  }, [hindranceProjects, hindranceProjectId])

  const effectiveHindranceProjectId = useMemo(() => {
    return selectedHindranceProject?.id || hindranceProjectId || ''
  }, [selectedHindranceProject, hindranceProjectId])

  const selectedHindrancePlaceId = selectedHindranceProject?.placeId

  const allowedHindrancePlaceIds = useMemo(() => {
    if (!selectedHindrancePlaceId) return new Set<string>()
    return unitSubtreeIds(places, selectedHindrancePlaceId)
  }, [places, selectedHindrancePlaceId])

  const hindrancePlaces = useMemo(() => {
    const list = places.filter((p) => !p.archived && allowedHindrancePlaceIds.has(p.id || ''))
    return list.length > 0 ? list : places.filter((p) => !p.archived)
  }, [places, allowedHindrancePlaceIds])

  const hindrancePlaceOptions: SelectOption<string>[] = useMemo(() => {
    return hindrancePlaces.map((p) => ({
      value: p.id || '',
      label: `${p.name} (${p.level})`,
    }))
  }, [hindrancePlaces])

  const effectiveHindrancePlaceId = useMemo(() => {
    if (hindrancePlaceId && hindrancePlaces.some((p) => p.id === hindrancePlaceId)) {
      return hindrancePlaceId
    }
    return selectedHindranceProject?.placeId || hindrancePlaces[0]?.id || places[0]?.id || ''
  }, [hindrancePlaceId, hindrancePlaces, selectedHindranceProject, places])

  const hindranceProjectOptions: SelectOption<string>[] = useMemo(() => {
    return hindranceProjects.map((p) => ({
      value: p.id,
      label: `${p.name} (${p.code})`,
    }))
  }, [hindranceProjects])

  const handleScanPlaceForHindrance = async (scannedText: string) => {
    let token = scannedText.trim()
    if (token.includes('/pq/')) {
      token = token.split('/pq/')[1]?.split('?')[0] || token
    } else if (token.includes('/q/')) {
      token = token.split('/q/')[1]?.split('?')[0] || token
    }
    setHindranceScannerOpen(false)
    const directMatch = places.find((p) => p.id === token)
    if (directMatch?.id) {
      setHindrancePlaceId(directMatch.id)
      return
    }
    try {
      const res = await resolvePlaceToken({ token })
      if (res?.placeId) {
        setHindrancePlaceId(res.placeId)
      } else {
        setHindrancePlaceId(token)
      }
    } catch {
      setHindrancePlaceId(token)
    }
  }

  const handleRaiseHindrance = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!hindranceDescription.trim()) return
    setHindranceBusy(true)
    setHindranceError(null)
    setHindranceSuccess(null)
    setHindrancePhotoFailure(null)

    const opId = createOpId()
    const routerCtx = getSharedDeviceWorkRouterContext()
    const isOffline = typeof navigator !== 'undefined' && !navigator.onLine
    const title = hindranceDescription.trim().slice(0, 100)
    const now = Date.now()

    try {
      const validatedTitle = validateConIssueTitle(title.length < 3 ? `${title} (hindrance)` : title)
      const validatedDesc = validateConIssueDescription(hindranceDescription)
      const validatedParty = validateConResponsibleParty({
        type: hindrancePartyType,
        name: hindrancePartyType,
      })
      const validatedImpact = validateConImpact({
        time: hindranceImpactTime,
        cost: hindranceImpactCost,
      })

      const payload: OpsConIssueCreatePayload = {
        workspaceId,
        projectId: effectiveHindranceProjectId,
        placeId: effectiveHindrancePlaceId,
        kind: 'HINDRANCE',
        title: validatedTitle,
        description: validatedDesc,
        firstNoticedAt: now,
        responseDueAt: now + 7 * 24 * 60 * 60 * 1000,
        responsibleParty: validatedParty,
        impact: validatedImpact,
        clientMutationId: opId,
        opId,
      }

      if (routerCtx) {
        if (hindrancePhotos.length > 0) {
          await queueOpsConIssueCreate(payload, hindrancePhotos)
        } else {
          await queueOpsConIssueCreate(payload)
        }
        setHindrancePhotos([])
      } else if (isOffline) {
        if (hindrancePhotos.length > 0) {
          await queueOpsConIssueCreate(payload, hindrancePhotos)
        } else {
          await queueOpsConIssueCreate(payload)
        }
        setHindrancePhotos([])
      } else {
        const createRes = await createConIssue(payload)
        const createdIssueId = createRes.issueId
        if (hindrancePhotos.length > 0) {
          const uploadedPaths: string[] = []
          const failedFiles: File[] = []
          for (const file of hindrancePhotos) {
            try {
              const up = await uploadConIssuePhoto(workspaceId, createdIssueId, file)
              uploadedPaths.push(up.path)
            } catch {
              failedFiles.push(file)
            }
          }
          if (uploadedPaths.length > 0) {
            try {
              await attachConIssuePhotos({
                workspaceId,
                issueId: createdIssueId,
                photoPaths: uploadedPaths,
                opId: `${opId}_photos`,
              })
            } catch {
              failedFiles.push(...hindrancePhotos)
            }
          }
          if (failedFiles.length > 0) {
            setHindrancePhotoFailure({ count: failedFiles.length, failedFiles, issueId: createdIssueId })
          } else {
            setHindrancePhotos([])
          }
        }
      }
      setHindranceDescription('')
      setHindranceSuccess(t('NLSHelperOpsHindranceSuccess'))
    } catch (err: unknown) {
      setHindranceError(getApiErrorMessage(err))
    } finally {
      setHindranceBusy(false)
    }
  }

  const handleRetryFailedHindrancePhotos = async () => {
    if (!hindrancePhotoFailure) return
    setHindranceBusy(true)
    setHindranceError(null)
    const { issueId, failedFiles } = hindrancePhotoFailure
    try {
      const uploadedPaths: string[] = []
      const remainingFailed: File[] = []
      for (const file of failedFiles) {
        try {
          const up = await uploadConIssuePhoto(workspaceId, issueId, file)
          uploadedPaths.push(up.path)
        } catch {
          remainingFailed.push(file)
        }
      }
      if (uploadedPaths.length > 0) {
        await attachConIssuePhotos({
          workspaceId,
          issueId,
          photoPaths: uploadedPaths,
          opId: `${createOpId()}_photos`,
        })
      }
      if (remainingFailed.length > 0) {
        setHindrancePhotoFailure({ count: remainingFailed.length, failedFiles: remainingFailed, issueId })
      } else {
        setHindrancePhotoFailure(null)
        setHindrancePhotos([])
      }
    } catch (err) {
      setHindranceError(getApiErrorMessage(err))
    } finally {
      setHindranceBusy(false)
    }
  }

  // Attendance roll
  useEffect(() => {
    if (!workspaceId || !canRoll) return
    const dayKey = getTodayDayKey()
    const unsub = subscribeAttendance(workspaceId, { dayKey }, (attList) => {
      setAttendanceRecords(attList || [])
    })
    return () => unsub()
  }, [workspaceId, canRoll])

  const effectiveAttendanceRecords = useMemo(() => {
    if (shouldUseOfflineCache && offlineCacheSnapshot?.opsAttendance) {
      return offlineCacheSnapshot.opsAttendance
    }
    return attendanceRecords
  }, [shouldUseOfflineCache, offlineCacheSnapshot, attendanceRecords])

  // Cache templates and latest approved versions for floor dues
  const templateIdsKey = useMemo(() => {
    return Array.from(new Set(floorDues.map((d) => d.templateId).filter(Boolean)))
      .sort()
      .join(',')
  }, [floorDues])

  useEffect(() => {
    if (!isSharedDevice || !currentUser || !templateIdsKey) return
    const templateIds = templateIdsKey.split(',').filter(Boolean)
    const unsubs: Array<() => void> = []

    for (const tplId of templateIds) {
      let tplObj: OpsTemplate | null = null
      let verObj: OpsTemplateVersion | null = null

      const updateCached = () => {
        if (tplObj && verObj) {
          const t: OpsTemplate = tplObj
          const v: OpsTemplateVersion = verObj
          setCachedTemplates((prev) => {
            const existing = prev.find((p) => p.template.id === tplId)
            if (
              existing &&
              existing.template.id === t.id &&
              existing.version.id === v.id &&
              existing.version.version === v.version
            ) {
              return prev
            }
            const filtered = prev.filter((p) => p.template.id !== tplId)
            return [...filtered, { template: t, version: v }]
          })
        }
      }

      unsubs.push(
        subscribeToTemplate(tplId, (tpl) => {
          tplObj = tpl
          updateCached()
        }),
      )
      unsubs.push(
        subscribeToTemplateVersions(
          tplId,
          (versions) => {
            const latest = versions[versions.length - 1]
            if (latest) {
              verObj = latest
              updateCached()
            }
          },
          undefined,
          { approvedOnly: true },
        ),
      )
    }

    return () => {
      for (const u of unsubs) u()
    }
  }, [isSharedDevice, currentUser, templateIdsKey])

  // Debounced cache persistence when online on shared device
  useEffect(() => {
    if (!isSharedDevice || !currentUser) return
    const timer = setTimeout(() => {
      if (getActivePersonCache()) {
        void updateActivePersonCache((prev) => ({
          ...prev,
          opsPhoneItems: allowedItems,
          opsDues: floorDues,
          opsRequests: myRequests,
          opsRequestKinds: requestKinds,
          opsTemplates: cachedTemplates,
          opsAttendance: attendanceRecords,
        }))
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [
    isSharedDevice,
    currentUser,
    allowedItems,
    floorDues,
    myRequests,
    requestKinds,
    cachedTemplates,
    attendanceRecords,
  ])

  const handleMarkRoll = async (
    record: OpsAttendance,
    status: 'PRESENT' | 'ABSENT' | 'LATE',
  ) => {
    const pId =
      record.personUid ||
      (record.personRef?.kind === 'WORKER'
        ? record.personRef.workerId
        : record.personRef?.uid) ||
      record.id
    setRollBusyId(pId)
    setRollError(null)
    const opId = createOpId()

    const rollPayload: MarkAttendanceRollInput = {
      workspaceId,
      dayKey: record.dayKey || getTodayDayKey(),
      shiftId: record.shiftId || 'shift-default',
      entries: [
        {
          personRef: record.personRef || { kind: 'MEMBER' as const, uid: record.personUid || '' },
          status,
        },
      ],
      opId,
    }

    const routerCtx = getSharedDeviceWorkRouterContext()

    try {
      if (routerCtx) {
        await routeAttendanceRoll(
          {
            ...rollPayload,
            clientMutationId: opId,
          },
          routerCtx,
        )
      } else if (typeof navigator !== 'undefined' && !navigator.onLine) {
        await queueOpsAttendanceRoll({
          ...rollPayload,
          clientMutationId: opId,
        })
      } else {
        await markAttendanceRoll(rollPayload)
      }
    } catch (err) {
      setRollError(getApiErrorMessage(err))
    } finally {
      setRollBusyId(null)
    }
  }

  // Helper sync store for queue status
  const [snapshot, setSnapshot] = useState<HelperStoreSnapshot>(() => getHelperSnapshot())
  useEffect(() => {
    return subscribeHelperStore(setSnapshot)
  }, [])

  const opsPending = useMemo(() => {
    return snapshot.pending.filter((m) => m.type.startsWith('OPS_'))
  }, [snapshot.pending])

  const opsRefused = useMemo(() => {
    return snapshot.failed.filter((m) => m.type.startsWith('OPS_'))
  }, [snapshot.failed])

  const handleDismissRefused = (recordId: string) => {
    void removeMutation(recordId)
  }

  const kindOptions: SelectOption<string>[] = useMemo(
    () =>
      filteredKinds.map((k) => ({
        value: k.key,
        label: k.label || k.labelKey || k.key,
      })),
    [filteredKinds],
  )

  const placeOptions: SelectOption<string>[] = useMemo(
    () =>
      places.map((p) => ({
        value: p.id || '',
        label: p.name || p.id || '',
      })),
    [places],
  )

  // Empty state when helper has no allowed ops items
  if (itemsLoaded && effectiveAllowedItems.length === 0) {
    return (
      <main className={styles.container} aria-label={t('NLSHelperOpsTabTitle')}>
        <div className={styles.section} data-testid="ops-no-items">
          <p className={styles.emptyText}>{t('NLSHelperOpsNoAllowedItems')}</p>
        </div>
      </main>
    )
  }

  return (
    <main className={styles.container} aria-label={t('NLSHelperOpsTabTitle')}>
      {/* h.checkIn: Place scan */}
      {canCheckIn && (
        <section
          className={styles.section}
          data-testid="ops-section-checkin"
          aria-labelledby="ops-checkin-title"
        >
          <h2 id="ops-checkin-title" className={styles.sectionTitle}>
            {t('NLSHelperOpsCheckInSectionTitle')}
          </h2>
          <p className={styles.sectionDesc}>{t('NLSHelperOpsScanPlaceDesc')}</p>
          <div>
            <Button
              variant="primary"
              size="lg"
              data-testid="ops-scan-place-btn"
              onClick={() => setScannerOpen(true)}
            >
              {t('NLSHelperOpsScanPlaceBtn')}
            </Button>
          </div>
          <BarcodeScannerShell
            open={scannerOpen}
            onClose={() => setScannerOpen(false)}
            onScan={handleScanPlace}
          />
        </section>
      )}

      {/* h.dueChecks: My due checks */}
      {canDueChecks && (
        <section
          className={styles.section}
          data-testid="ops-section-due"
          aria-labelledby="ops-due-title"
        >
          <h2 id="ops-due-title" className={styles.sectionTitle}>
            {t('NLSHelperOpsDueSectionTitle')}
          </h2>
          {unitFilterPlaces.length > 0 && (
            <div className={styles.unitFilterRow} data-testid="due-unit-filter">
              <Select
                value={selectedDueUnitId}
                options={[
                  { value: '', label: t('NLSHelperOpsAllUnits') },
                  ...unitFilterPlaces.map((u) => ({ value: u.id || '', label: u.name })),
                ]}
                onChange={(val) => setSelectedDueUnitId(String(val))}
                ariaLabel={t('NLSHelperOpsFilterUnitLabel')}
              />
            </div>
          )}
          {!recordingDue && dueError && (
            <div className={styles.errorBanner} role="alert">
              {dueError}
            </div>
          )}
          {displayedDues.length === 0 ? (
            <p className={styles.emptyText}>{t('NLSHelperOpsDuesEmpty')}</p>
          ) : (
            <ul className={styles.cardList}>
              {displayedDues.map((due) => {
                const isBusy = dueActionBusy === due.id
                const dueEnriched = due as OpsDue & { templateTitle?: string; placeName?: string }
                return (
                  <li key={due.id} className={styles.cardItem}>
                    <div className={styles.cardHeader}>
                      <div>
                        <h3 className={styles.cardTitle}>
                          {dueEnriched.templateTitle || due.templateId || 'Due inspection'}
                        </h3>
                        {Boolean(dueEnriched.placeName || due.placeId) && (
                          <p className={styles.cardSubtitle}>
                            {dueEnriched.placeName || due.placeId}
                          </p>
                        )}
                      </div>
                      <StatusPill tone={due.status === 'DONE' ? 'success' : 'neutral'}>
                        {due.status}
                      </StatusPill>
                    </div>
                    <div className={styles.cardActions}>
                      <Button
                        variant="primary"
                        size="sm"
                        data-testid={`due-record-${due.id}`}
                        disabled={isBusy}
                        onClick={() => handleRecordDue(due)}
                      >
                        {t('NLSHelperOpsRecordNowBtn')}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        data-testid={`due-scan-${due.id}`}
                        disabled={isBusy}
                        onClick={() => handleScanCheckpoint(due)}
                      >
                        {t('NLSHelperOpsScanCheckpointBtn')}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        data-testid={`due-skip-${due.id}`}
                        disabled={isBusy}
                        onClick={() => {
                          setSkippingDue(due)
                          setSkipReason('')
                        }}
                      >
                        {t('NLSHelperOpsSkipBtn')}
                      </Button>
                    </div>
                  </li>
                )
              })}
            </ul>
          )}

          {recordingDue && (
            <OpsRecordModalForm
              open={Boolean(recordingDue)}
              workspaceId={workspaceId}
              templateId={recordingDue.templateId}
              placeId={recordingDue.placeId}
              dueId={recordingDue.id}
              formTestId="due-record-form"
              submitTestId={`due-record-submit-${recordingDue.id}`}
              fieldTestIdPrefix="due"
              submitLabel={t('NLSHelperOpsRecordNowBtn')}
              isSharedDevice={isSharedDevice}
              devicePlaceId={effectiveDevicePlaceId}
              cachedTemplate={
                (offlineCacheSnapshot?.opsTemplates || cachedTemplates).find(
                  (t) => t.template.id === recordingDue.templateId,
                )?.template
              }
              cachedVersion={
                (offlineCacheSnapshot?.opsTemplates || cachedTemplates).find(
                  (t) => t.template.id === recordingDue.templateId,
                )?.version
              }
              onDismiss={() => {
                setRecordingDue(null)
              }}
              onSubmit={async (payload) => {
                const routerCtx = getSharedDeviceWorkRouterContext()
                if (routerCtx) {
                  await routeRegisterRecord(
                    {
                      ...payload,
                      clientMutationId: payload.opId,
                    },
                    routerCtx,
                  )
                } else if (typeof navigator !== 'undefined' && !navigator.onLine) {
                  await queueOpsDueRecord({
                    ...payload,
                    clientMutationId: payload.opId,
                  })
                } else {
                  await submitRecord(payload)
                }
              }}
              onSuccess={() => {
                setRecordingDue(null)
              }}
            />
          )}

          {skippingDue && (
            <AppDialog
              open={Boolean(skippingDue)}
              title={t('NLSHelperOpsSkipTitle')}
              onDismiss={() => setSkippingDue(null)}
              actions={[
                {
                  label: t('NLSCommonActionCancel'),
                  variant: 'secondary',
                  onClick: () => setSkippingDue(null),
                },
                {
                  label: t('NLSHelperOpsSkipConfirm'),
                  variant: 'primary',
                  onClick: handleConfirmSkipDue,
                  disabled: !skipReason.trim(),
                  testId: `due-skip-confirm-${skippingDue.id}`,
                },
              ]}
            >
              <div className={styles.dialogBody}>
                <TextInput
                  value={skipReason}
                  onChange={(val) => setSkipReason(val)}
                  placeholder={t('NLSHelperOpsSkipReasonPlaceholder')}
                  data-testid={`due-skip-reason-${skippingDue.id}`}
                />
              </div>
            </AppDialog>
          )}

          <BarcodeScannerShell
            open={Boolean(scanningCheckpointDue)}
            onClose={() => setScanningCheckpointDue(null)}
            onScan={handleCheckpointScanned}
          />
        </section>
      )}

      {/* h.request: Raise request & My requests */}
      {canRequest && (
        <section
          className={styles.section}
          data-testid="ops-section-request"
          aria-labelledby="ops-request-title"
        >
          <h2 id="ops-request-title" className={styles.sectionTitle}>
            {t('NLSHelperOpsRequestsSectionTitle')}
          </h2>

          <form className={styles.form} onSubmit={handleRaiseRequest}>
            <h3 className={styles.cardTitle}>{t('NLSHelperOpsRaiseRequestTitle')}</h3>
            {requestError && (
              <div className={styles.errorBanner} role="alert">
                {requestError}
              </div>
            )}
            {requestSuccess && (
              <div className={styles.successBanner} role="status">
                {requestSuccess}
              </div>
            )}
            {kindOptions.length > 0 && (
              <div className={styles.formField}>
                <Select
                  value={effectiveKindId}
                  options={kindOptions}
                  onChange={(val) => setSelectedKindId(val)}
                  ariaLabel={t('NLSHelperOpsKindPickerPlaceholder')}
                />
              </div>
            )}
            {!isSharedDevice && placeOptions.length > 0 && (
              <div className={styles.formField}>
                <Select
                  value={effectivePlaceId}
                  options={placeOptions}
                  onChange={(val) => setSelectedPlaceId(val)}
                  ariaLabel={t('NLSHelperOpsPlacePickerPlaceholder')}
                />
              </div>
            )}
            {!isSharedDevice && (
              <div className={styles.formField}>
                <Button
                  variant="secondary"
                  size="sm"
                  type="button"
                  data-testid="ops-raise-scan-place-btn"
                  onClick={() => setRaiseScannerOpen(true)}
                >
                  {t('NLSHelperOpsScanPlaceBtn')}
                </Button>
              </div>
            )}
            <div className={styles.formField}>
              <TextInput
                value={requestTitle}
                onChange={(val) => setRequestTitle(val)}
                placeholder={t('NLSHelperOpsRequestTextPlaceholder')}
                data-testid="ops-request-title-input"
              />
            </div>
            <div>
              <Button
                variant="primary"
                size="md"
                data-testid="ops-raise-request-submit"
                disabled={requestBusy || !requestTitle.trim()}
                onClick={handleRaiseRequest}
              >
                {t('NLSHelperOpsSubmitRequestBtn')}
              </Button>
            </div>
          </form>

          <BarcodeScannerShell
            open={raiseScannerOpen}
            onClose={() => setRaiseScannerOpen(false)}
            onScan={handleScanPlaceForRaise}
          />

          <div>
            <h3 className={styles.cardTitle}>{t('NLSHelperOpsMyRequestsTitle')}</h3>
            {displayedRequests.length === 0 ? (
              <p className={styles.emptyText}>{t('NLSHelperOpsMyRequestsEmpty')}</p>
            ) : (
              <ul className={styles.cardList}>
                {displayedRequests.map((req) => {
                  const isHighlighted = effectiveRequestId === req.id
                  const reqPlace = places.find((p) => p.id === req.placeId)
                  const isBedOrRoom =
                    Boolean(reqPlace) && (reqPlace?.level === 'BED' || reqPlace?.level === 'ROOM')
                  const isBed = Boolean(reqPlace) && reqPlace?.level === 'BED'
                  const bedState = isBed && reqPlace?.id ? bedStatesMap.get(reqPlace.id)?.state || 'READY' : null

                  return (
                    <li
                      key={req.id}
                      className={styles.cardItem}
                      data-testid={`ops-request-item-${req.id}`}
                      data-highlighted={isHighlighted ? 'true' : 'false'}
                    >
                      <div className={styles.cardHeader}>
                        <div className={styles.cardTitleGroup}>
                          <h4 className={styles.cardTitle}>{req.title}</h4>
                          {isBedOrRoom && reqPlace && (
                            <span
                              className={styles.requestPlaceName}
                              data-testid={`request-place-${req.id}`}
                            >
                              {reqPlace.name}
                            </span>
                          )}
                          {isBed && (
                            <span
                              className={styles.bedStateChip}
                              data-testid={`request-bed-chip-${req.id}`}
                            >
                              {bedState}
                            </span>
                          )}
                        </div>
                        <StatusPill
                          tone={
                            req.state === 'DONE' || req.state === 'VERIFIED'
                              ? 'success'
                              : req.state === 'IN_PROGRESS'
                                ? 'info'
                                : 'neutral'
                          }
                        >
                          {req.state}
                        </StatusPill>
                      </div>

                      {req.assigneeUid === currentUid && (
                        <div className={styles.cardActions}>
                          {(req.state === 'OPEN' || req.state === 'ACKNOWLEDGED' || (req.state as string) === 'ASSIGNED') && (
                            <Button
                              variant="secondary"
                              size="sm"
                              data-testid={`request-start-${req.id}`}
                              disabled={startBusyId === req.id}
                              onClick={() => handleStartRequest(req)}
                            >
                              {t('NLSHelperOpsStartRequestBtn')}
                            </Button>
                          )}
                          {req.state === 'IN_PROGRESS' && (
                            <Button
                              variant="primary"
                              size="sm"
                              data-testid={`request-done-${req.id}`}
                              onClick={() => handleOpenDone(req)}
                            >
                              {t('NLSHelperOpsDoneRequestBtn')}
                            </Button>
                          )}
                        </div>
                      )}
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        </section>
      )}

      {/* Mark Done Dialog */}
      {doneTargetRequest && (
        <AppDialog
          open={Boolean(doneTargetRequest)}
          title={t('NLSHelperOpsMarkDoneTitle')}
          onDismiss={() => {
            setDoneTargetRequest(null)
            setDonePhotoFiles([])
          }}
          actions={[
            {
              label: t('NLSCommonActionCancel'),
              variant: 'secondary',
              onClick: () => {
                setDoneTargetRequest(null)
                setDonePhotoFiles([])
              },
              disabled: doneBusy,
            },
            {
              label: t('NLSCommonActionConfirm'),
              variant: 'primary',
              onClick: handleConfirmDone,
              disabled: doneBusy || (isDonePhotoRequired && donePhotoFiles.length === 0),
            },
          ]}
        >
          <div className={styles.dialogBody}>
            {isDonePhotoRequired ? (
              <div className={styles.formField}>
                <label className={styles.inputLabel}>
                  {t('NLSHelperOpsDonePhotoTitle')}
                </label>
                <HiddenFileInput
                  testId="done-photo-input"
                  accept="image/jpeg,image/png,image/webp,image/heic"
                  multiple
                  onFiles={(files) => {
                    setDonePhotoFiles(Array.from(files).slice(0, 4))
                  }}
                />
              </div>
            ) : (
              <p>
                {t('NLSHelperOpsMarkDoneConfirm')}
              </p>
            )}
          </div>
        </AppDialog>
      )}

      {/* h.roll: Attendance roll */}
      {canRoll && (
        <section
          className={styles.section}
          data-testid="ops-section-roll"
          aria-labelledby="ops-roll-title"
        >
          <h2 id="ops-roll-title" className={styles.sectionTitle}>
            {t('NLSHelperOpsRollSectionTitle')}
          </h2>
          {rollError && (
            <div className={styles.errorBanner} role="alert">
              {rollError}
            </div>
          )}
          {effectiveAttendanceRecords.length === 0 ? (
            <p className={styles.emptyText}>{t('NLSHelperOpsRollEmpty')}</p>
          ) : (
            <ul className={styles.cardList}>
              {effectiveAttendanceRecords.map((att) => {
                const pId =
                  att.personUid ||
                  (att.personRef?.kind === 'WORKER'
                    ? att.personRef.workerId
                    : att.personRef?.uid) ||
                  att.id
                const pName =
                  (att.personRef?.kind === 'WORKER' ? att.personRef.name : null) ||
                  att.personUid ||
                  'Worker'
                const isBusy = rollBusyId === pId

                return (
                  <li key={att.id} className={styles.cardItem}>
                    <div className={styles.cardHeader}>
                      <h3 className={styles.cardTitle}>{pName}</h3>
                      <StatusPill tone="neutral">{att.status}</StatusPill>
                    </div>
                    <div className={styles.cardActions}>
                      <Button
                        variant="secondary"
                        size="sm"
                        data-testid={`roll-mark-present-${pId}`}
                        disabled={isBusy}
                        onClick={() => handleMarkRoll(att, 'PRESENT')}
                      >
                        {t('NLSHelperOpsMarkPresent')}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        data-testid={`roll-mark-absent-${pId}`}
                        disabled={isBusy}
                        onClick={() => handleMarkRoll(att, 'ABSENT')}
                      >
                        {t('NLSHelperOpsMarkAbsent')}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        data-testid={`roll-mark-late-${pId}`}
                        disabled={isBusy}
                        onClick={() => handleMarkRoll(att, 'LATE')}
                      >
                        {t('NLSHelperOpsMarkLate')}
                      </Button>
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </section>
      )}

      {/* h.bmw: Biomedical Waste Entry */}
      {canBmw && (
        <BmwPhoneSection
          workspaceId={workspaceId}
          callerUid={currentUid}
          callerRole="HELPER"
          isOwner={false}
          allowCorrection={false}
          places={places}
          isSharedDevice={isSharedDevice}
          effectiveDevicePlaceId={effectiveDevicePlaceId}
        />
      )}

      {/* h.dpr: Construction Daily Progress Report */}
      {canDpr && (
        <DprPhoneSection
          workspaceId={workspaceId}
          callerUid={currentUid}
          callerRole="HELPER"
          places={places}
          isSharedDevice={isSharedDevice}
        />
      )}

      {/* h.hindrance: Raise Construction Hindrance */}
      {canHindrance && (
        <section className={styles.section} data-testid="hindrance-raise-form">
          <div className={styles.cardHeader}>
            <div className={styles.cardTitleGroup}>
              <h2 className={styles.sectionTitle}>{t('NLSHelperOpsHindranceSectionTitle')}</h2>
              <p className={styles.sectionDesc}>{t('NLSHelperOpsHindranceSectionDesc')}</p>
            </div>
          </div>

          {hindranceError && (
            <div className={styles.errorBanner} role="alert">
              {hindranceError}
            </div>
          )}

          {hindranceSuccess && (
            <div className={styles.successBanner}>
              {hindranceSuccess}
            </div>
          )}

          <form onSubmit={handleRaiseHindrance} className={styles.form}>
            {hindranceProjectOptions.length > 0 && (
              <div className={styles.formField}>
                <label className={styles.inputLabel} htmlFor="hindrance-project-select">
                  {t('NLSHelperOpsHindranceProjectLabel')}
                </label>
                <Select
                  id="hindrance-project-select"
                  value={effectiveHindranceProjectId}
                  options={hindranceProjectOptions}
                  onChange={(val) => setHindranceProjectId(val)}
                  disabled={hindranceBusy}
                  ariaLabel={t('NLSHelperOpsHindranceProjectLabel')}
                />
              </div>
            )}

            <div className={styles.formField}>
              <label className={styles.inputLabel} htmlFor="hindrance-place-select">
                {t('NLSHelperOpsHindrancePlaceLabel')}
              </label>
              <div className={styles.scanPlaceRow}>
                <div className={styles.scanPlaceSelect}>
                  <Select
                    id="hindrance-place-select"
                    value={effectiveHindrancePlaceId}
                    options={hindrancePlaceOptions}
                    onChange={(val) => setHindrancePlaceId(val)}
                    disabled={hindranceBusy}
                    ariaLabel={t('NLSHelperOpsHindrancePlaceLabel')}
                  />
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  type="button"
                  data-testid="hindrance-scan-place-btn"
                  onClick={() => setHindranceScannerOpen(true)}
                  disabled={hindranceBusy}
                >
                  {t('NLSHelperOpsScanPlaceBtn')}
                </Button>
              </div>
            </div>

            <div className={styles.formField}>
              <label className={styles.inputLabel} htmlFor="hindrance-desc-input">
                {t('NLSHelperOpsHindranceDescLabel')}
              </label>
              <TextInput
                id="hindrance-desc-input"
                data-testid="hindrance-description-input"
                value={hindranceDescription}
                onChange={(val) => setHindranceDescription(val)}
                placeholder={t('NLSHelperOpsHindranceDescLabel')}
                disabled={hindranceBusy}
              />
            </div>

            <div className={styles.formField}>
              <label className={styles.inputLabel} htmlFor="hindrance-party-select">
                {t('NLSHelperOpsHindrancePartyLabel')}
              </label>
              <Select
                id="hindrance-party-select"
                value={hindrancePartyType}
                options={CON_RESPONSIBLE_PARTY_TYPES.map((pt) => ({ value: pt, label: pt }))}
                onChange={(val) => setHindrancePartyType(val as ConResponsiblePartyType)}
                disabled={hindranceBusy}
                ariaLabel={t('NLSHelperOpsHindrancePartyLabel')}
              />
            </div>

            <div className={styles.gridTwoCol}>
              <div className={styles.formField}>
                <label className={styles.inputLabel} htmlFor="hindrance-impact-time-select">
                  {t('NLSHelperOpsHindranceImpactLabel')} (Time)
                </label>
                <Select
                  id="hindrance-impact-time-select"
                  value={hindranceImpactTime}
                  options={CON_IMPACT_LEVELS.map((il) => ({ value: il, label: il }))}
                  onChange={(val) => setHindranceImpactTime(val as ConImpactLevel)}
                  disabled={hindranceBusy}
                  ariaLabel={`${t('NLSHelperOpsHindranceImpactLabel')} Time`}
                />
              </div>

              <div className={styles.formField}>
                <label className={styles.inputLabel} htmlFor="hindrance-impact-cost-select">
                  {t('NLSHelperOpsHindranceImpactLabel')} (Cost)
                </label>
                <Select
                  id="hindrance-impact-cost-select"
                  value={hindranceImpactCost}
                  options={CON_IMPACT_LEVELS.map((il) => ({ value: il, label: il }))}
                  onChange={(val) => setHindranceImpactCost(val as ConImpactLevel)}
                  disabled={hindranceBusy}
                  ariaLabel={`${t('NLSHelperOpsHindranceImpactLabel')} Cost`}
                />
              </div>
            </div>

            <div className={styles.formField}>
              <label className={styles.inputLabel} htmlFor="hindrance-photos-btn">
                {t('NLSOpsConIssuesPhotos')}
              </label>
              <Button
                id="hindrance-photos-btn"
                variant="secondary"
                size="sm"
                type="button"
                onClick={() => hindranceFileInputRef.current?.click()}
                disabled={hindranceBusy}
              >
                {t('NLSOpsConIssuesAddPhotos')}
              </Button>
              <HiddenFileInput
                ref={hindranceFileInputRef}
                testId="hindrance-photos-input"
                accept="image/jpeg,image/png,image/webp,image/heic"
                multiple
                disabled={hindranceBusy}
                onFiles={(fileList) => {
                  const files = Array.from(fileList)
                  setHindrancePhotos((prev) => [...prev, ...files].slice(0, MAX_CON_ISSUE_PHOTOS))
                }}
              />
              {hindrancePhotos.length > 0 && (
                <span className={styles.emptyText}>
                  {hindrancePhotos.length} / {MAX_CON_ISSUE_PHOTOS}
                </span>
              )}
            </div>

            {hindrancePhotoFailure && (
              <div role="alert" className={styles.errorBanner}>
                <span>{t('NLSOpsConIssuesPhotosFailed', { count: String(hindrancePhotoFailure.count) })}</span>
                <Button
                  variant="secondary"
                  size="sm"
                  type="button"
                  disabled={hindranceBusy}
                  onClick={handleRetryFailedHindrancePhotos}
                >
                  {t('NLSOpsConIssuesPhotosRetry')}
                </Button>
              </div>
            )}

            <div>
              <Button
                variant="primary"
                size="md"
                data-testid="hindrance-submit-btn"
                disabled={hindranceBusy || !hindranceDescription.trim()}
                type="submit"
              >
                {t('NLSHelperOpsHindranceSubmitBtn')}
              </Button>
            </div>
          </form>

          <BarcodeScannerShell
            open={hindranceScannerOpen}
            onClose={() => setHindranceScannerOpen(false)}
            onScan={handleScanPlaceForHindrance}
          />
        </section>
      )}

      {/* h.oncall: Hospital On-Call Duty */}
      {canOnCall && (
        <OnCallPhoneSection
          workspaceId={workspaceId}
          callerUid={currentUid}
          callerRole="HELPER"
          surface="helper"
        />
      )}

      {/* h.plant: Plant Operations */}
      {canPlant && (
        <PlantHelperPhoneSection
          workspaceId={workspaceId}
          places={places}
          assets={plantAssets}
          currentUid={currentUid}
          isSharedDevice={isSharedDevice}
        />
      )}

      {/* Offline queue status */}
      <section
        className={styles.section}
        data-testid="ops-queue-section"
        aria-labelledby="ops-queue-title"
      >
        <h2 id="ops-queue-title" className={styles.sectionTitle}>
          {t('NLSHelperOpsQueueSectionTitle')}
        </h2>
        {opsPending.length > 0 && (
          <p className={styles.sectionDesc}>
            {t('NLSHelperOpsQueuePendingCount', { count: opsPending.length })}
          </p>
        )}
        <h3 className={styles.cardTitle}>{t('NLSHelperOpsQueueRefusedTitle')}</h3>
        {opsRefused.length === 0 ? (
          <p className={styles.emptyText}>{t('NLSHelperOpsQueueRefusedEmpty')}</p>
        ) : (
          <ul className={styles.queueList}>
            {opsRefused.map((item: HelperMutationRecord) => (
              <li
                key={item.id}
                className={`${styles.queueItem} ${styles.queueItemRefused}`}
              >
                <div className={styles.refusedText}>
                  {item.error || t('NLSHelperOpsQueueRefusedFallback')}
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  data-testid={`dismiss-refused-${item.id}`}
                  onClick={() => handleDismissRefused(item.id)}
                >
                  {t('NLSHelperOpsQueueDismissBtn')}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
  )
}
